import React from "react";
import { dateFormatter } from "../../utils/dateUtils";
import { formatCurrency } from "../../constants/utils";
import { getPriceAdjustmentConfig } from "../../constants/priceAdjustment";
import DiscountTooltip from "../../components/common/DiscountTooltip";

export const getDateRangeAsString = (st, ed) => {
  if (!st && !ed) return "N/A";
  if (!st && ed) return "Till " + dateFormatter(ed);
  if (st && !ed) return "From " + dateFormatter(st);
  return dateFormatter(st) + " - " + dateFormatter(ed);
};

/**
 * Stay-date labels for the list row, one per range (EO-1492 multi-range).
 * Falls back to the legacy single stayDates pair for responses that predate
 * the stayDateRanges field. An all-null range formats to "N/A" and is dropped
 * so the cell's own emptyText handles the evergreen case.
 */
export const getStayDateRangeLabels = (promo) => {
  const ranges =
    Array.isArray(promo?.stayDateRanges) && promo.stayDateRanges.length > 0
      ? promo.stayDateRanges
      : [promo?.stayDates];
  return ranges
    .map((range) => getDateRangeAsString(range?.start, range?.end))
    .filter((label) => label !== "N/A");
};

const STATUS_UI_TO_API = {
  active: "Active",
  inactive: "Inactive",
  draft: "Draft",
  deleted: "Deleted",
};

export const mapStatusFilterToApi = (uiValue) =>
  STATUS_UI_TO_API[uiValue] || null;

export const mapTypeFilterToApi = (uiValue) =>
  ["smart", "custom", "buyer_campaign"].includes(uiValue) ? uiValue : null;

export const getStatusFilterValue = ({ status }) => {
  if (status === "Active") return "active";
  if (status === "Inactive") return "inactive";
  if (status === "Draft") return "draft";
  if (status === "Deleted") return "deleted";
  return "all";
};

// Deleted On / Deleted By are unavailable for non-pilot properties until the backend
// records them, so null is the expected case here, not an error.
export const formatDeletedOn = (value) => {
  if (!value) return "N/A";
  return dateFormatter(String(value).split("T")[0]);
};

export const getTypeFilterValue = ({ promoType }) => {
  if (promoType === "smart") return "smart";
  if (promoType === "custom") return "custom";
  if (promoType === "buyer_campaign") return "buyer_campaign";
  return "all";
};

export const isSmartPromo = (promo) => promo.promoType === "Smart";

export const renderDiscount = ({
  promo,
  index,
  isPilotHotel,
  selectedProperty,
}) => {
  const { prefix, colorClass } = getPriceAdjustmentConfig(
    promo.priceAdjustment
  );

  // Pilot hotel: show "Multi" badge for derived-from-derived promos
  if (isPilotHotel && promo.derivedRate && promo.discountValue === "Multi") {
    return <span className="derived-badge">Multi</span>;
  }

  if (promo?.isDoW && !promo.discountValue) {
    const discountMap = promo.discountMap;
    const currencyCode = promo.currencyCode;

    return (
      <DiscountTooltip
        discountMap={discountMap || {}}
        discountType={promo.discountType}
        targetId={`discount-${promo.id || index}`}
        currencyCode={currencyCode}
        priceAdjustment={promo.priceAdjustment}
      />
    );
  }

  const discountValue = promo.discountValue || 0;

  // For flat discount (discountType === "1"), use formatCurrency with currency symbol
  if (promo.discountType === "1") {
    const currency = selectedProperty.acceptedCurrencies?.[0] || "USD";
    return (
      <span className={colorClass}>
        {prefix}
        {formatCurrency(Number.parseFloat(discountValue), currency)}
      </span>
    );
  }

  // For percentage discount (discountType === "2"), show with % symbol
  return (
    <span className={colorClass}>
      {prefix}
      {discountValue}%
    </span>
  );
};
